import type { CongViecDuAnCheck, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { TableRecord } from '@/components/common/ActionPlan/CongViecDuAn/Right/Table/types';
import { toActionPlanCalendarDate } from '@/components/common/ActionPlanV2/core/date';
import { APV2_ROOT_TASK_PARENT_ID, isApv2RootTaskParentId } from '@/components/common/ActionPlanV2/core/mucTieu';

// #region fields

/** Tỉ trọng AP — cặp manual / cal (leaf, không import module V2 khác). */

export type Apv2TiTrongVersion = 1 | 2;

export type Apv2TiTrongSibling = {
  id: string;
  tiTrong: number | null | undefined;
  tiTrongCal: number | null | undefined;
};

export function apv2RoundTiTrong(value: number): number {
  return Math.round(value * 100) / 100;
}

export function apv2MutualExclusiveFields(
  tiTrong: number | null | undefined,
  tiTrongCal: number | null | undefined
): { tiTrong: number | null; tiTrongCal: number | null } {
  const hasManual =
    tiTrong !== null && tiTrong !== undefined && Number.isFinite(Number(tiTrong));
  if (hasManual) {
    return { tiTrong: Number(tiTrong), tiTrongCal: null };
  }
  const hasCal =
    tiTrongCal !== null && tiTrongCal !== undefined && Number.isFinite(Number(tiTrongCal));
  if (hasCal) {
    return { tiTrong: null, tiTrongCal: Number(tiTrongCal) };
  }
  return { tiTrong: null, tiTrongCal: null };
}

export function apv2IsLockedManualTiTrong(
  tiTrong: number | null | undefined,
  tiTrongCal: number | null | undefined
): boolean {
  return apv2MutualExclusiveFields(tiTrong, tiTrongCal).tiTrong !== null;
}

export function apv2GetEffectiveTiTrong(
  tiTrong: number | null | undefined,
  tiTrongCal: number | null | undefined
): number {
  const ex = apv2MutualExclusiveFields(tiTrong, tiTrongCal);
  if (ex.tiTrong !== null) return ex.tiTrong;
  if (ex.tiTrongCal !== null) return ex.tiTrongCal;
  return 0;
}

export const getEffectiveWeightVer1 = (
  tiTrong: number | null | undefined,
  tiTrongCal: number | null | undefined
): number => apv2GetEffectiveTiTrong(tiTrong, tiTrongCal);

export const getEffectiveWeightVer2 = (
  tiTrongV2: number | null | undefined,
  tiTrongCalV2: number | null | undefined
): number => apv2GetEffectiveTiTrong(tiTrongV2, tiTrongCalV2);

export function apv2ToTreeCongViecTiTrong(
  tiTrong: number | null,
  tiTrongCal: number | null
): { tiTrong: number | undefined; tiTrongCal: number | undefined } {
  return {
    tiTrong: tiTrong === null ? undefined : tiTrong,
    tiTrongCal: tiTrongCal === null ? undefined : tiTrongCal,
  };
}

// #endregion fields

// #region apv2TiTrong

/**
 * Tỉ trọng AP — hai cặp độc lập:
 * Ver 1: tiTrong / tiTrongCal
 * Ver 2: tiTrongV2 / tiTrongCalV2
 * Effective: manual ?? cal (không dùng 0 thay null khi chưa có giá trị — display dùng ?? 0).
 */

function readPairFromCheck(
  c: CongViecDuAnCheck,
  version: Apv2TiTrongVersion
): { tiTrong: number | null; tiTrongCal: number | null } {
  return version === 1
    ? apv2MutualExclusiveFields(c.tiTrong, c.tiTrongCal)
    : apv2MutualExclusiveFields(c.tiTrongV2, c.tiTrongCalV2);
}

function readPairFromCongViec(
  cv: TableRecord['congViec'] | undefined,
  version: Apv2TiTrongVersion
): { tiTrong: number | null; tiTrongCal: number | null } {
  if (!cv) return { tiTrong: null, tiTrongCal: null };
  return version === 1
    ? apv2MutualExclusiveFields(cv.tiTrong, cv.tiTrongCal)
    : apv2MutualExclusiveFields(cv.tiTrongV2, cv.tiTrongCalV2);
}

function patchCheckPair(
  item: CongViecDuAnCheck,
  version: Apv2TiTrongVersion,
  tiTrong: number | null,
  tiTrongCal: number | null,
  markChange = true
): CongViecDuAnCheck {
  const ex = apv2MutualExclusiveFields(tiTrong, tiTrongCal);
  if (version === 1) {
    return {
      ...item,
      tiTrong: ex.tiTrong,
      tiTrongCal: ex.tiTrongCal,
      ...(markChange ? { hasChange: true } : {}),
    };
  }
  return {
    ...item,
    tiTrongV2: ex.tiTrong,
    tiTrongCalV2: ex.tiTrongCal,
    ...(markChange ? { hasChange: true } : {}),
  };
}

function applyPairToCongViec(
  cv: TableRecord['congViec'],
  version: Apv2TiTrongVersion,
  tiTrong: number | null,
  tiTrongCal: number | null
): TableRecord['congViec'] {
  const ex = apv2MutualExclusiveFields(tiTrong, tiTrongCal);
  const toTree = (v: number | null) => (v === null ? undefined : v);
  if (version === 1) {
    return { ...cv, tiTrong: toTree(ex.tiTrong), tiTrongCal: toTree(ex.tiTrongCal) };
  }
  return { ...cv, tiTrongV2: toTree(ex.tiTrong), tiTrongCalV2: toTree(ex.tiTrongCal) };
}

/** So sánh tỉ trọng hiệu lực (manual ?? cal) — tránh dirty sibling khi vẫn = 100 (paste root). */
function effectiveTiTrongChanged(
  before: { tiTrong: number | null; tiTrongCal: number | null },
  after: { tiTrong: number | null; tiTrongCal: number | null }
): boolean {
  return (
    apv2RoundTiTrong(apv2GetEffectiveTiTrong(before.tiTrong, before.tiTrongCal)) !==
    apv2RoundTiTrong(apv2GetEffectiveTiTrong(after.tiTrong, after.tiTrongCal))
  );
}

export function apv2NormalizeCheckListTiTrong(
  checkList: CongViecDuAnCheck[]
): CongViecDuAnCheck[] {
  return checkList.map((c) => {
    const v1 = apv2MutualExclusiveFields(c.tiTrong, c.tiTrongCal);
    const v2 = apv2MutualExclusiveFields(c.tiTrongV2, c.tiTrongCalV2);
    if (
      c.tiTrong === v1.tiTrong &&
      c.tiTrongCal === v1.tiTrongCal &&
      c.tiTrongV2 === v2.tiTrong &&
      c.tiTrongCalV2 === v2.tiTrongCal
    ) {
      return c;
    }
    return {
      ...c,
      tiTrong: v1.tiTrong,
      tiTrongCal: v1.tiTrongCal,
      tiTrongV2: v2.tiTrong,
      tiTrongCalV2: v2.tiTrongCal,
    };
  });
}

function findParentInTree(tree: TableRecord[], parentId: string): TableRecord | null {
  const target = String(parentId);
  for (const node of tree) {
    const id = String(node.congViec?.id ?? node.key ?? '');
    if (id === target) return node;
    if (node.childs?.length) {
      const found = findParentInTree(node.childs as TableRecord[], target);
      if (found) return found;
    }
  }
  return null;
}

function findRecordInTree(tree: TableRecord[], taskId: string): TableRecord | null {
  return findParentInTree(tree, String(taskId));
}

function collectManualOnTreeParent(
  tree: TableRecord[],
  parentId: string,
  version: Apv2TiTrongVersion,
  exceptId?: string
): Map<string, number> {
  const map = new Map<string, number>();
  const parent = findParentInTree(tree, parentId);
  if (!parent?.childs?.length) return map;
  for (const child of parent.childs as TableRecord[]) {
    const id = String(child.congViec?.id ?? child.key ?? '');
    if (!id || id === exceptId) continue;
    const ex = readPairFromCongViec(child.congViec, version);
    if (ex.tiTrong !== null) map.set(id, ex.tiTrong);
  }
  return map;
}

function mergeSiblingRow(
  id: string,
  treeEx: { tiTrong: number | null; tiTrongCal: number | null },
  check: CongViecDuAnCheck | undefined,
  version: Apv2TiTrongVersion
): Apv2TiTrongSibling {
  const checkEx = check
    ? readPairFromCheck(check, version)
    : { tiTrong: null, tiTrongCal: null };
  if (treeEx.tiTrong !== null) return { id, tiTrong: treeEx.tiTrong, tiTrongCal: null };
  if (checkEx.tiTrong !== null) return { id, tiTrong: checkEx.tiTrong, tiTrongCal: null };
  if (treeEx.tiTrongCal !== null) return { id, tiTrong: null, tiTrongCal: treeEx.tiTrongCal };
  if (checkEx.tiTrongCal !== null) return { id, tiTrong: null, tiTrongCal: checkEx.tiTrongCal };
  return { id, tiTrong: null, tiTrongCal: null };
}

function getSiblingsFromTree(
  tree: TableRecord[],
  parentId: string,
  checkList: CongViecDuAnCheck[],
  version: Apv2TiTrongVersion,
  override?: { id: string; tiTrong: number | null; tiTrongCal: number | null }
): Apv2TiTrongSibling[] {
  const overrideId = override ? String(override.id) : '';
  const byId = new Map<string, Apv2TiTrongSibling>();

  const parent = findParentInTree(tree, parentId);
  if (parent?.childs?.length) {
    for (const child of parent.childs as TableRecord[]) {
      const id = String(child.congViec?.id ?? child.key ?? '');
      if (!id) continue;
      if (override && id === overrideId) {
        const ex = apv2MutualExclusiveFields(override.tiTrong, override.tiTrongCal);
        byId.set(id, { id, tiTrong: ex.tiTrong, tiTrongCal: ex.tiTrongCal });
        continue;
      }
      const treeEx = readPairFromCongViec(child.congViec, version);
      const check = checkList.find((c) => String(c.id) === id);
      byId.set(id, mergeSiblingRow(id, treeEx, check, version));
    }
  }

  if (override && !byId.has(overrideId)) {
    const ex = apv2MutualExclusiveFields(override.tiTrong, override.tiTrongCal);
    byId.set(overrideId, { id: overrideId, tiTrong: ex.tiTrong, tiTrongCal: ex.tiTrongCal });
  }

  // Gom thêm siblings chỉ có trong check list (không nằm trong tree subset hiển thị)
  // — để maxAllowed / chia Cal không bỏ sót anh em ẩn.
  const pid = String(parentId);
  for (const c of checkList) {
    const id = String(c.id);
    if (!id || byId.has(id)) continue;
    if (String(c.parentID ?? '') !== pid) continue;
    const ex = readPairFromCheck(c, version);
    byId.set(id, { id, tiTrong: ex.tiTrong, tiTrongCal: ex.tiTrongCal });
  }

  return Array.from(byId.values());
}
/**
 * Budget cha khi chia sibling:
 * - Ver 2: tỉ trọng hiệu lực V2 của cha (forest root → mỗi root 100, không chia)
 * - Ver 1: forest root / con trực tiếp root mỗi cái 100; cấp dưới chia budget V1 cha
 */
export function apv2ResolveParentContext(
  record: TableRecord,
  checkList: CongViecDuAnCheck[],
  tree?: TableRecord[],
  version: Apv2TiTrongVersion = 2
): { parentId: string; parentBudget: number; isDirectUnderRoot: boolean } | null {
  const parentId = String(record.congViec?.parentID ?? '').trim();
  if (!parentId || parentId === 'undefined') return null;

  const isForestRootRow = isApv2RootTaskParentId(parentId);
  let parentIsForestRoot = false;
  if (!isForestRootRow) {
    if (tree?.length) {
      const parentNode = findParentInTree(tree, parentId);
      parentIsForestRoot = !!parentNode && isApv2RootTaskParentId(String(parentNode.congViec?.parentID ?? ''));
    }
    if (!parentIsForestRoot) {
      const parentCheck = checkList.find((c) => String(c.id) === parentId);
      parentIsForestRoot = !!parentCheck && isApv2RootTaskParentId(String(parentCheck.parentID ?? ''));
    }
  }
  // V1: forest root hoặc con trực tiếp dưới forest root → cố định 100
  const isDirectUnderRoot = isForestRootRow || parentIsForestRoot;

  if (version === 1) {
    if (isDirectUnderRoot) {
      return { parentId, parentBudget: 100, isDirectUnderRoot: true };
    }
    if (tree?.length) {
      const parentNode = findParentInTree(tree, parentId);
      if (parentNode) {
        const fromTree = apv2GetEffectiveTiTrong(
          ...pairTuple(readPairFromCongViec(parentNode.congViec, 1))
        );
        if (fromTree > 0) return { parentId, parentBudget: fromTree, isDirectUnderRoot: false };
      }
    }
    const parentCheck = checkList.find((c) => String(c.id) === parentId);
    if (parentCheck) {
      const fromCheck = apv2GetEffectiveTiTrong(...pairTuple(readPairFromCheck(parentCheck, 1)));
      if (fromCheck > 0) return { parentId, parentBudget: fromCheck, isDirectUnderRoot: false };
    }
    return { parentId, parentBudget: 100, isDirectUnderRoot: false };
  }

  // Ver 2
  if (isForestRootRow) {
    return { parentId, parentBudget: 100, isDirectUnderRoot: true };
  }

  if (tree?.length) {
    const parentNode = findParentInTree(tree, parentId);
    if (parentNode) {
      const fromTree = apv2GetEffectiveTiTrong(
        ...pairTuple(readPairFromCongViec(parentNode.congViec, 2))
      );
      if (fromTree > 0) return { parentId, parentBudget: fromTree, isDirectUnderRoot: false };
    }
  }

  const parentCheck = checkList.find((c) => String(c.id) === parentId);
  if (parentCheck) {
    const fromCheck = apv2GetEffectiveTiTrong(...pairTuple(readPairFromCheck(parentCheck, 2)));
    if (fromCheck > 0) return { parentId, parentBudget: fromCheck, isDirectUnderRoot: false };
    // Cha có trong check list nhưng chưa có budget V2 → mặc định 100 để vẫn cap + chia anh em
    return { parentId, parentBudget: 100, isDirectUnderRoot: false };
  }

  return null;
}

function pairTuple(ex: {
  tiTrong: number | null;
  tiTrongCal: number | null;
}): [number | null, number | null] {
  return [ex.tiTrong, ex.tiTrongCal];
}

function splitTiTrongCalEvenly(
  parentBudget: number,
  siblings: Apv2TiTrongSibling[],
  lockManualId?: string
): Map<string, number> {
  const map = new Map<string, number>();
  const lockId = lockManualId ? String(lockManualId) : '';
  let manualTotal = 0;
  for (const s of siblings) {
    const isLocked = s.id === lockId || apv2IsLockedManualTiTrong(s.tiTrong, s.tiTrongCal);
    if (isLocked) manualTotal += Number(s.tiTrong ?? 0);
  }
  const autoSiblings = siblings.filter(
    (s) => s.id !== lockId && !apv2IsLockedManualTiTrong(s.tiTrong, s.tiTrongCal)
  );
  const remaining = Math.max(0, parentBudget - manualTotal);
  if (autoSiblings.length === 0) return map;
  const base = apv2RoundTiTrong(remaining / autoSiblings.length);
  const diff = remaining - base * autoSiblings.length;
  autoSiblings.forEach((s, i) => {
    const isLast = i === autoSiblings.length - 1;
    map.set(s.id, isLast ? apv2RoundTiTrong(base + diff) : base);
  });
  return map;
}

function restoreManualSiblingsOnCheckList(
  checkList: CongViecDuAnCheck[],
  manualOnTree: Map<string, number>,
  tree: TableRecord[],
  parentId: string,
  version: Apv2TiTrongVersion,
  skipId?: string
): CongViecDuAnCheck[] {
  if (manualOnTree.size === 0) return checkList;
  let next = checkList;
  for (const [id, tiTrong] of manualOnTree) {
    if (skipId && id === skipId) continue;
    if (!next.some((c) => String(c.id) === id)) {
      next = upsertCheckFromTreeNode(next, tree, id, parentId);
    }
    next = next.map((c) =>
      String(c.id) === id ? patchCheckPair(c, version, tiTrong, null) : c
    );
  }
  return next;
}

function applyCalMapToCheckList(
  checkList: CongViecDuAnCheck[],
  calMap: Map<string, number>,
  version: Apv2TiTrongVersion,
  manualIds?: Set<string>,
  /** false = không đánh hasChange checklist khi chỉ rewrite field cùng effective. */
  markHasChangeOnEffectiveChange = true
): CongViecDuAnCheck[] {
  if (calMap.size === 0) return checkList;
  return checkList.map((c) => {
    const id = String(c.id);
    if (!calMap.has(id)) return c;
    if (manualIds?.has(id)) return c;
    const pair = readPairFromCheck(c, version);
    if (apv2IsLockedManualTiTrong(pair.tiTrong, pair.tiTrongCal)) return c;
    const nextCal = calMap.get(id)!;
    const after = { tiTrong: null as number | null, tiTrongCal: nextCal };
    const dirty =
      markHasChangeOnEffectiveChange && effectiveTiTrongChanged(pair, after);
    return patchCheckPair(c, version, null, nextCal, dirty);
  });
}

function syncCheckToTree(
  tree: TableRecord[],
  checkList: CongViecDuAnCheck[],
  version: Apv2TiTrongVersion,
  ids?: Set<string>,
  /** true = paste/add-child: chỉ dirty khi tỉ trọng hiệu lực thực sự đổi. */
  markHasChange = false
): TableRecord[] {
  const byId = new Map(checkList.map((c) => [String(c.id), c]));
  const patch = (nodes: TableRecord[]): TableRecord[] =>
    nodes.map((node) => {
      let next = node;
      const id = String(node.congViec?.id ?? node.key ?? '');
      const check = id ? byId.get(id) : undefined;
      if (check && (!ids || ids.has(id))) {
        const before = readPairFromCongViec(node.congViec, version);
        const pair = readPairFromCheck(check, version);
        const cv = applyPairToCongViec(node.congViec, version, pair.tiTrong, pair.tiTrongCal);
        const fieldsChanged =
          version === 1
            ? node.congViec.tiTrong !== cv.tiTrong || node.congViec.tiTrongCal !== cv.tiTrongCal
            : node.congViec.tiTrongV2 !== cv.tiTrongV2 ||
              node.congViec.tiTrongCalV2 !== cv.tiTrongCalV2;
        if (fieldsChanged) {
          const dirty =
            markHasChange && effectiveTiTrongChanged(before, pair);
          next = {
            ...node,
            ...(dirty ? { hasChange: true } : {}),
            congViec: cv,
          };
        }
      }
      if (node.childs?.length) {
        const childs = patch(node.childs as TableRecord[]);
        if (childs !== node.childs) next = { ...next, childs };
      }
      return next;
    });
  return patch(tree);
}

function applyCalMapToTree(
  tree: TableRecord[],
  parentId: string,
  calMap: Map<string, number>,
  version: Apv2TiTrongVersion,
  markHasChange = false
): TableRecord[] {
  if (calMap.size === 0) return tree;
  const parent = findParentInTree(tree, parentId);
  const childIds = new Set(
    (parent?.childs as TableRecord[] | undefined)?.map((c) =>
      String(c.congViec?.id ?? c.key ?? '')
    ) ?? []
  );
  const patch = (nodes: TableRecord[]): TableRecord[] =>
    nodes.map((node) => {
      let next = node;
      const id = String(node.congViec?.id ?? node.key ?? '');
      if (id && childIds.has(id) && calMap.has(id)) {
        const before = readPairFromCongViec(node.congViec, version);
        const nextCal = calMap.get(id)!;
        const after = { tiTrong: null as number | null, tiTrongCal: nextCal };
        const cv = applyPairToCongViec(node.congViec, version, null, nextCal);
        const fieldsChanged =
          version === 1
            ? node.congViec.tiTrong !== cv.tiTrong || node.congViec.tiTrongCal !== cv.tiTrongCal
            : node.congViec.tiTrongV2 !== cv.tiTrongV2 ||
              node.congViec.tiTrongCalV2 !== cv.tiTrongCalV2;
        if (fieldsChanged) {
          const dirty = markHasChange && effectiveTiTrongChanged(before, after);
          next = {
            ...node,
            ...(dirty ? { hasChange: true } : {}),
            congViec: cv,
          };
        }
      }
      if (node.childs?.length) {
        const childs = patch(node.childs as TableRecord[]);
        if (childs !== node.childs) next = { ...next, childs };
      }
      return next;
    });
  return patch(tree);
}

/** Đánh dirty node user vừa sửa tỉ trọng — cascade hasChangeInTree lúc sanitize/save. */
function markTreeNodeHasChange(tree: TableRecord[], taskId: string): TableRecord[] {
  const idTarget = String(taskId);
  if (!idTarget) return tree;
  const patch = (nodes: TableRecord[]): TableRecord[] =>
    nodes.map((node) => {
      const id = String(node.congViec?.id ?? node.key ?? '');
      let next = node;
      if (id === idTarget && node.hasChange !== true) {
        next = { ...node, hasChange: true };
      }
      if (node.childs?.length) {
        const childs = patch(node.childs as TableRecord[]);
        if (childs !== node.childs) next = { ...next, childs };
      }
      return next;
    });
  return patch(tree);
}

function upsertCheckFromTreeNode(
  checkList: CongViecDuAnCheck[],
  tree: TableRecord[],
  taskId: string,
  parentId: string
): CongViecDuAnCheck[] {
  const id = String(taskId);
  if (checkList.some((c) => String(c.id) === id)) return checkList;
  const node = findRecordInTree(tree, id);
  if (!node) return checkList;
  const v1 = readPairFromCongViec(node.congViec, 1);
  const v2 = readPairFromCongViec(node.congViec, 2);
  return [
    ...checkList,
    {
      id,
      parentID: parentId,
      nganSach: Number(node.congViec?.nganSach ?? 0),
      tiTrong: v1.tiTrong,
      tiTrongCal: v1.tiTrongCal,
      tiTrongV2: v2.tiTrong,
      tiTrongCalV2: v2.tiTrongCal,
      ngayBatDau: toActionPlanCalendarDate(node.congViec?.ngayBatDau),
      ngayKetThuc: toActionPlanCalendarDate(node.congViec?.ngayKetThuc),
      viTri: (node.congViec?.viTri ?? null) as number | null,
    },
  ];
}

/** Ver 1 dưới root / con trực tiếp root: mỗi sibling = 100 (Cal).
 * Chỉ sync field — không bao giờ dirty (APV2 đánh hasChange theo V2). */
function assignV1DirectUnderRoot(
  tree: TableRecord[],
  checkList: CongViecDuAnCheck[],
  parentId: string,
  editing?: { id: string; tiTrong: number | null; tiTrongCal: number | null; lockAsManual: boolean }
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  let nextCheck = checkList;
  const siblings = getSiblingsFromTree(tree, parentId, nextCheck, 1);
  for (const s of siblings) {
    nextCheck = upsertCheckFromTreeNode(nextCheck, tree, s.id, parentId);
    const isEditing = editing && String(editing.id) === s.id;
    if (isEditing && editing.lockAsManual && editing.tiTrong !== null) {
      nextCheck = nextCheck.map((c) =>
        String(c.id) === s.id ? patchCheckPair(c, 1, editing.tiTrong, null) : c
      );
    } else {
      // V1 sync field only — không gắn hasChange (APV2 dirty theo V2).
      nextCheck = nextCheck.map((c) =>
        String(c.id) === s.id ? patchCheckPair(c, 1, null, 100, false) : c
      );
    }
  }
  const ids = new Set(siblings.map((s) => s.id));
  const nextTree = syncCheckToTree(tree, nextCheck, 1, ids, false);
  return { tree: nextTree, checkList: apv2NormalizeCheckListTiTrong(nextCheck) };
}

/** Forest root Ver 2: mỗi node CalV2 = 100 (không chia giữa các root). */
function assignV2ForestRootsFixed100(
  tree: TableRecord[],
  checkList: CongViecDuAnCheck[],
  parentId: string,
  editing?: { id: string; tiTrong: number | null; tiTrongCal: number | null; lockAsManual: boolean },
  markHasChange = false
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  let nextCheck = checkList;
  const siblings = getSiblingsFromTree(tree, parentId, nextCheck, 2);
  for (const s of siblings) {
    nextCheck = upsertCheckFromTreeNode(nextCheck, tree, s.id, parentId);
    const isEditing = editing && String(editing.id) === s.id;
    if (isEditing && editing.lockAsManual && editing.tiTrong !== null) {
      nextCheck = nextCheck.map((c) =>
        String(c.id) === s.id ? patchCheckPair(c, 2, editing.tiTrong, null) : c
      );
      continue;
    }
    // Root đã = 100 hiệu lực → không đụng / không dirty (paste thêm root khác).
    const treeNode = findRecordInTree(tree, s.id);
    const before = treeNode
      ? readPairFromCongViec(treeNode.congViec, 2)
      : { tiTrong: s.tiTrong ?? null, tiTrongCal: s.tiTrongCal ?? null };
    const after = { tiTrong: null as number | null, tiTrongCal: 100 };
    if (!effectiveTiTrongChanged(before, after)) continue;
    nextCheck = nextCheck.map((c) =>
      String(c.id) === s.id ? patchCheckPair(c, 2, null, 100, markHasChange) : c
    );
  }
  const ids = new Set(siblings.map((s) => s.id));
  const nextTree = syncCheckToTree(tree, nextCheck, 2, ids, markHasChange);
  return { tree: nextTree, checkList: apv2NormalizeCheckListTiTrong(nextCheck) };
}

function redistributeTiTrongCalForParent(
  tree: TableRecord[],
  checkList: CongViecDuAnCheck[],
  parentId: string,
  parentBudget: number,
  version: Apv2TiTrongVersion,
  editing?: { id: string; tiTrong: number | null; tiTrongCal: number | null; lockAsManual: boolean },
  markHasChange = false
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  if (isApv2RootTaskParentId(parentId)) {
    return version === 1
      ? assignV1DirectUnderRoot(tree, checkList, parentId, editing)
      : assignV2ForestRootsFixed100(tree, checkList, parentId, editing, markHasChange);
  }
  // V1: con trực tiếp dưới forest root → mỗi Cal=100
  if (version === 1) {
    const parentNode = findParentInTree(tree, parentId);
    if (parentNode && isApv2RootTaskParentId(String(parentNode.congViec?.parentID ?? ''))) {
      return assignV1DirectUnderRoot(tree, checkList, parentId, editing);
    }
  }

  const editingId = editing ? String(editing.id) : undefined;
  const manualOnTree = collectManualOnTreeParent(tree, parentId, version, editingId);

  let nextCheck = restoreManualSiblingsOnCheckList(
    checkList,
    manualOnTree,
    tree,
    parentId,
    version,
    editingId
  );
  if (editing) {
    nextCheck = upsertCheckFromTreeNode(nextCheck, tree, editing.id, parentId);
  }

  const override = editing
    ? { id: editing.id, tiTrong: editing.tiTrong, tiTrongCal: editing.tiTrongCal }
    : undefined;
  const siblings = getSiblingsFromTree(tree, parentId, nextCheck, version, override);
  const lockManualId = editing?.lockAsManual ? editing.id : undefined;
  const calMap = splitTiTrongCalEvenly(parentBudget, siblings, lockManualId);

  if (editing) {
    nextCheck = nextCheck.map((c) =>
      String(c.id) === editing.id
        ? patchCheckPair(c, version, editing.tiTrong, editing.tiTrongCal)
        : c
    );
  }
  nextCheck = applyCalMapToCheckList(
    nextCheck,
    calMap,
    version,
    new Set(manualOnTree.keys()),
    markHasChange
  );
  nextCheck = restoreManualSiblingsOnCheckList(
    nextCheck,
    manualOnTree,
    tree,
    parentId,
    version,
    editingId
  );

  const allSiblingIds = new Set(siblings.map((s) => s.id));
  let nextTree = applyCalMapToTree(tree, parentId, calMap, version, markHasChange);
  nextTree = syncCheckToTree(nextTree, nextCheck, version, allSiblingIds, markHasChange);
  return {
    tree: nextTree,
    checkList: apv2NormalizeCheckListTiTrong(nextCheck),
  };
}

export type Apv2TiTrongBlurParams = {
  tree: TableRecord[];
  checkList: CongViecDuAnCheck[];
  record: TableRecord;
  taskId: string | number;
  trimmedInput: string;
  version?: Apv2TiTrongVersion;
};

export type Apv2TiTrongBlurResult = {
  tree: TableRecord[];
  checkList: CongViecDuAnCheck[];
  nextTiTrong: number | undefined;
  rejected: boolean;
};

export function applyApv2TiTrongBlurEdit(params: Apv2TiTrongBlurParams): Apv2TiTrongBlurResult {
  const version = params.version ?? 2;
  const { tree, checkList, taskId, trimmedInput } = params;
  const taskIdStr = String(taskId);
  const isClear = trimmedInput === '';
  const parentCtx = apv2ResolveParentContext(params.record, checkList, tree, version);

  const finishOk = (
    nextTree: TableRecord[],
    nextCheck: CongViecDuAnCheck[],
    nextTiTrong: number | undefined
  ): Apv2TiTrongBlurResult => ({
    tree: markTreeNodeHasChange(nextTree, taskIdStr),
    checkList: nextCheck,
    nextTiTrong,
    rejected: false,
  });

  if (!parentCtx) {
    if (!isClear && (Number.isNaN(Number(trimmedInput)) || !Number.isFinite(Number(trimmedInput)))) {
      return { tree, checkList, nextTiTrong: undefined, rejected: false };
    }
    const manual = isClear ? null : apv2RoundTiTrong(Math.max(0, Number(trimmedInput)));
    const nextCheck = checkList.map((c) =>
      String(c.id) === taskIdStr ? patchCheckPair(c, version, manual, null) : c
    );
    const nextTree = syncCheckToTree(tree, nextCheck, version, new Set([taskIdStr]));
    return finishOk(nextTree, nextCheck, manual ?? undefined);
  }

  const { parentId, parentBudget, isDirectUnderRoot } = parentCtx;

  // Ver 1: con trực tiếp root luôn 100 — không nhận nhập tay khác
  if (version === 1 && isDirectUnderRoot) {
    const result = assignV1DirectUnderRoot(tree, checkList, parentId);
    return finishOk(result.tree, result.checkList, 100);
  }

  if (isClear) {
    const result = redistributeTiTrongCalForParent(
      tree,
      checkList,
      parentId,
      parentBudget,
      version,
      { id: taskIdStr, tiTrong: null, tiTrongCal: null, lockAsManual: false }
    );
    const self = result.checkList.find((c) => String(c.id) === taskIdStr);
    const display = self
      ? apv2GetEffectiveTiTrong(...pairTuple(readPairFromCheck(self, version)))
      : undefined;
    return finishOk(result.tree, result.checkList, display);
  }

  const raw = Math.max(0, Number(trimmedInput));
  if (!Number.isFinite(raw)) {
    return { tree, checkList, nextTiTrong: undefined, rejected: true };
  }

  const siblings = getSiblingsFromTree(tree, parentId, checkList, version);
  const maxAllowed = apv2ComputeMaxTiTrongInput(parentBudget, taskId, siblings);
  const manual = apv2RoundTiTrong(Math.min(raw, maxAllowed));

  const result = redistributeTiTrongCalForParent(
    tree,
    checkList,
    parentId,
    parentBudget,
    version,
    { id: taskIdStr, tiTrong: manual, tiTrongCal: null, lockAsManual: true }
  );
  return finishOk(result.tree, result.checkList, manual);
}

export function apv2AutoTiTrongForParent(
  tree: TableRecord[],
  parentId: string,
  parentBudget: number,
  checkList: CongViecDuAnCheck[] = [],
  version: Apv2TiTrongVersion = 2,
  markHasChange = false
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  return redistributeTiTrongCalForParent(
    tree,
    checkList,
    parentId,
    parentBudget,
    version,
    undefined,
    markHasChange
  );
}

/** Thêm/xóa/paste trên table APV2: chỉ chia lại V2 (không đụng dirty/check V1). */
export function apv2AutoTiTrongV2ForParent(
  tree: TableRecord[],
  parentId: string,
  checkList: CongViecDuAnCheck[] = [],
  markHasChange = false
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  // Forest root → mỗi cây CalV2 = 100 (không chia giữa các root).
  if (isApv2RootTaskParentId(parentId)) {
    return assignV2ForestRootsFixed100(tree, checkList, parentId, undefined, markHasChange);
  }
  const parentNode = findParentInTree(tree, parentId);
  const budgetV2 = parentNode
    ? apv2GetEffectiveTiTrong(...pairTuple(readPairFromCongViec(parentNode.congViec, 2)))
    : 0;
  return apv2AutoTiTrongForParent(
    tree,
    parentId,
    budgetV2 > 0 ? budgetV2 : 100,
    checkList,
    2,
    markHasChange
  );
}

/** Bổ sung con của `parentId` vào check list rồi chia lại CalV2 — 1 bước dùng chung add/paste. */
export function apv2SyncTiTrongV2ForParent(
  tree: TableRecord[],
  parentId: string,
  checkList: CongViecDuAnCheck[],
  toCheck: (t: TableRecord, parentIdOverride?: string) => CongViecDuAnCheck,
  markHasChange = true
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  const withChildren = apv2EnsureCheckListHasTreeChildren(tree, parentId, checkList, toCheck);
  return apv2AutoTiTrongV2ForParent(tree, parentId, withChildren, markHasChange);
}

/**
 * Chia lại CalV2 cho MỌI cấp trong subtree `rootId` (paste subtree nhiều tầng).
 * Duyệt root → lá (BFS): budget V2 của cha đã có trước khi chia cho con.
 */
export function apv2SyncTiTrongV2ForSubtree(
  tree: TableRecord[],
  rootId: string,
  checkList: CongViecDuAnCheck[],
  toCheck: (t: TableRecord, parentIdOverride?: string) => CongViecDuAnCheck,
  markHasChange = true
): { tree: TableRecord[]; checkList: CongViecDuAnCheck[] } {
  let nextTree = tree;
  let nextCheck = checkList;
  const queue: string[] = [String(rootId)];
  const seen = new Set<string>();

  while (queue.length > 0) {
    const parentId = queue.shift()!;
    if (!parentId || seen.has(parentId)) continue;
    seen.add(parentId);

    const childs = (findParentInTree(nextTree, parentId)?.childs as TableRecord[]) || [];
    if (childs.length === 0) continue;

    const synced = apv2SyncTiTrongV2ForParent(
      nextTree,
      parentId,
      nextCheck,
      toCheck,
      markHasChange
    );
    nextTree = synced.tree;
    nextCheck = synced.checkList;

    for (const child of childs) {
      const id = String(child.congViec?.id ?? child.key ?? '');
      if (id) queue.push(id);
    }
  }

  return { tree: nextTree, checkList: nextCheck };
}

export function apv2EnsureCheckListHasTreeChildren(
  tree: TableRecord[],
  parentId: string,
  checkList: CongViecDuAnCheck[],
  toCheck: (t: TableRecord, parentIdOverride?: string) => CongViecDuAnCheck
): CongViecDuAnCheck[] {
  const parent = findParentInTree(tree, String(parentId));
  if (!parent?.childs?.length) return checkList;

  const byId = new Map(checkList.map((c) => [String(c.id), { ...c }]));
  for (const child of parent.childs as TableRecord[]) {
    const id = String(child.congViec?.id ?? child.key ?? '');
    if (!id) continue;
    const fromTree = toCheck(child, parentId);
    const existing = byId.get(id);
    byId.set(
      id,
      existing
        ? (() => {
            const keepV1 = apv2IsLockedManualTiTrong(existing.tiTrong, existing.tiTrongCal);
            const keepV2 = apv2IsLockedManualTiTrong(existing.tiTrongV2, existing.tiTrongCalV2);
            const fromV1 = apv2MutualExclusiveFields(fromTree.tiTrong, fromTree.tiTrongCal);
            const fromV2 = apv2MutualExclusiveFields(fromTree.tiTrongV2, fromTree.tiTrongCalV2);
            const v1 = keepV1
              ? apv2MutualExclusiveFields(existing.tiTrong, existing.tiTrongCal)
              : fromV1;
            const v2 = keepV2
              ? apv2MutualExclusiveFields(existing.tiTrongV2, existing.tiTrongCalV2)
              : fromV2;
            return {
              ...existing,
              parentID: fromTree.parentID,
              tiTrong: v1.tiTrong,
              tiTrongCal: v1.tiTrongCal,
              tiTrongV2: v2.tiTrong,
              tiTrongCalV2: v2.tiTrongCal,
              ngayBatDau: fromTree.ngayBatDau ?? existing.ngayBatDau,
              ngayKetThuc: fromTree.ngayKetThuc ?? existing.ngayKetThuc,
              viTri: fromTree.viTri ?? existing.viTri,
            };
          })()
        : {
            ...fromTree,
            tiTrongV2: fromTree.tiTrongV2 ?? null,
            tiTrongCalV2: fromTree.tiTrongCalV2 ?? null,
          }
    );
  }
  return Array.from(byId.values());
}

export function apv2ComputeMaxTiTrongInput(
  parentBudget: number,
  currentTaskId: string | number,
  siblings: Apv2TiTrongSibling[]
): number {
  let fixed = 0;
  const currentId = String(currentTaskId);
  for (const s of siblings) {
    if (s.id === currentId) continue;
    if (apv2IsLockedManualTiTrong(s.tiTrong, s.tiTrongCal)) {
      fixed += Number(s.tiTrong ?? 0);
    }
  }
  return Math.max(0, apv2RoundTiTrong(parentBudget - fixed));
}

/** Độ sâu parent trong check list — root forest = 0 (duyệt root → lá để budget cha sẵn trước khi chia con). */
function apv2CheckParentDepth(
  parentId: string,
  byId: Map<string, CongViecDuAnCheck>,
  memo: Map<string, number>,
  visiting: Set<string> = new Set()
): number {
  if (isApv2RootTaskParentId(parentId)) return 0;
  const cached = memo.get(parentId);
  if (cached != null) return cached;
  // Chu kỳ ParentID (A→B→A hoặc self-parent) → coi như root để tránh stack overflow.
  if (visiting.has(parentId)) {
    memo.set(parentId, 0);
    return 0;
  }
  visiting.add(parentId);
  const node = byId.get(parentId);
  if (!node) {
    memo.set(parentId, 0);
    return 0;
  }
  const nextParentId = String(node.parentID ?? '');
  // Self-parent: id trỏ chính nó
  if (nextParentId === parentId) {
    memo.set(parentId, 0);
    return 0;
  }
  const depth = 1 + apv2CheckParentDepth(nextParentId, byId, memo, visiting);
  memo.set(parentId, depth);
  return depth;
}

/**
 * Trước save: tính lại Cal V1 + Cal V2 trên toàn bộ check list (không subset hiển thị).
 * Parent phải xử lý theo depth tăng dần — nếu lá trước cha thì budget=0 → bỏ qua (V1/V2 con sai/null).
 *
 * Tối ưu bigdata: build 1 lần `indexById` + `childrenByParent` (index), patch từng entry theo index —
 * không filter/find/map toàn list mỗi parent (trước là O(parents × list)).
 */
export function recalcApv2TiTrongOnFullCheckList(
  checkList: CongViecDuAnCheck[]
): CongViecDuAnCheck[] {
  const next = apv2NormalizeCheckListTiTrong(
    checkList.map((c) => ({
      ...c,
      tiTrongV2: c.tiTrongV2 ?? null,
      tiTrongCalV2: c.tiTrongCalV2 ?? null,
    }))
  );

  // Index id → vị trí trong mảng + nhóm con theo parent (index) — 1 pass O(m).
  const indexById = new Map<string, number>();
  const childrenByParent = new Map<string, number[]>();
  next.forEach((c, idx) => {
    indexById.set(String(c.id), idx);
    const pid = String(c.parentID ?? '');
    const arr = childrenByParent.get(pid);
    if (arr) arr.push(idx);
    else childrenByParent.set(pid, [idx]);
  });

  const parentIds = new Set(childrenByParent.keys());
  parentIds.add(APV2_ROOT_TASK_PARENT_ID);

  const byIdForDepth = new Map(next.map((c) => [String(c.id), c]));
  const depthMemo = new Map<string, number>();
  const orderedParentIds = [...parentIds].sort(
    (a, b) =>
      apv2CheckParentDepth(a, byIdForDepth, depthMemo) - apv2CheckParentDepth(b, byIdForDepth, depthMemo)
  );

  for (const parentId of orderedParentIds) {
    const childIndices = childrenByParent.get(parentId);
    if (!childIndices || childIndices.length === 0) continue;

    // Đọc parent từ `next` live (index) — không qua find O(m).
    const parentIdx = indexById.get(parentId);
    const parent = parentIdx != null ? next[parentIdx] : undefined;
    const parentIsForestRoot =
      !!parent && isApv2RootTaskParentId(String(parent.parentID ?? ''));

    // V1: forest roots + con trực tiếp root → mỗi Cal=100; cấp dưới chia budget V1 cha
    if (isApv2RootTaskParentId(parentId) || parentIsForestRoot) {
      for (const idx of childIndices) {
        const c = next[idx];
        if (apv2IsLockedManualTiTrong(c.tiTrong, c.tiTrongCal)) continue;
        next[idx] = patchCheckPair(c, 1, null, 100, false);
      }
    } else {
      const siblings: Apv2TiTrongSibling[] = childIndices.map((idx) => {
        const c = next[idx];
        const ex = readPairFromCheck(c, 1);
        return { id: String(c.id), tiTrong: ex.tiTrong, tiTrongCal: ex.tiTrongCal };
      });
      const budgetV1 = parent
        ? apv2GetEffectiveTiTrong(...pairTuple(readPairFromCheck(parent, 1)))
        : 100;
      if (budgetV1 > 0) {
        const calMap = splitTiTrongCalEvenly(budgetV1, siblings);
        if (calMap.size > 0) {
          for (const idx of childIndices) {
            const c = next[idx];
            if (!calMap.has(String(c.id))) continue;
            const pair = readPairFromCheck(c, 1);
            if (apv2IsLockedManualTiTrong(pair.tiTrong, pair.tiTrongCal)) continue;
            next[idx] = patchCheckPair(c, 1, null, calMap.get(String(c.id))!, false);
          }
        }
      }
    }

    // V2: forest roots → mỗi CalV2=100; còn lại chia budget V2 cha
    if (isApv2RootTaskParentId(parentId)) {
      for (const idx of childIndices) {
        const c = next[idx];
        if (apv2IsLockedManualTiTrong(c.tiTrongV2, c.tiTrongCalV2)) continue;
        next[idx] = patchCheckPair(c, 2, null, 100, false);
      }
      continue;
    }

    let budgetV2 = 100;
    if (parent) {
      budgetV2 = apv2GetEffectiveTiTrong(...pairTuple(readPairFromCheck(parent, 2)));
      if (budgetV2 <= 0) continue;
    }
    const siblingsV2: Apv2TiTrongSibling[] = childIndices.map((idx) => {
      const c = next[idx];
      const ex = readPairFromCheck(c, 2);
      return { id: String(c.id), tiTrong: ex.tiTrong, tiTrongCal: ex.tiTrongCal };
    });
    const calMapV2 = splitTiTrongCalEvenly(budgetV2, siblingsV2);
    if (calMapV2.size > 0) {
      for (const idx of childIndices) {
        const c = next[idx];
        if (!calMapV2.has(String(c.id))) continue;
        const pair = readPairFromCheck(c, 2);
        if (apv2IsLockedManualTiTrong(pair.tiTrong, pair.tiTrongCal)) continue;
        next[idx] = patchCheckPair(c, 2, null, calMapV2.get(String(c.id))!, false);
      }
    }
  }

  return apv2NormalizeCheckListTiTrong(next);
}

/** Apply 4 field tỉ trọng từ check list sang tree đang hiển thị. */
export function applyApv2CheckTiTrongToTree(
  tree: TableRecord[],
  checkList: CongViecDuAnCheck[]
): TableRecord[] {
  let next = syncCheckToTree(tree, checkList, 1);
  next = syncCheckToTree(next, checkList, 2);
  return next;
}

/**
 * Apply 4 field tỉ trọng từ check list sang tree (DuAnCongViecTree[]) dùng cho payload save.
 * Tương đương applyApv2CheckTiTrongToTree nhưng thao tác trực tiếp trên tree đã convert 1 lần —
 * tránh phải convert lại tableRecords → tree (deep clone) mỗi lần save.
 */
export function applyApv2CheckTiTrongToDuAnTree(
  tree: DuAnCongViecTree[],
  checkList: CongViecDuAnCheck[]
): DuAnCongViecTree[] {
  const byId = new Map(checkList.map((c) => [String(c.id), c]));
  const patch = (nodes: DuAnCongViecTree[], version: Apv2TiTrongVersion): DuAnCongViecTree[] =>
    nodes.map((node) => {
      let next = node;
      const id = String(node.congViec?.id ?? '');
      const check = id ? byId.get(id) : undefined;
      if (check) {
        const pair = readPairFromCheck(check, version);
        const cv = applyPairToCongViec(node.congViec, version, pair.tiTrong, pair.tiTrongCal);
        const changed =
          version === 1
            ? node.congViec.tiTrong !== cv.tiTrong || node.congViec.tiTrongCal !== cv.tiTrongCal
            : node.congViec.tiTrongV2 !== cv.tiTrongV2 ||
              node.congViec.tiTrongCalV2 !== cv.tiTrongCalV2;
        // Chỉ sync field tỉ trọng vào payload — không mark hasChange (tránh edit cha → mọi con bị BE update).
        if (changed) {
          next = { ...node, congViec: cv };
        }
      }
      if (node.childs?.length) {
        const childs = patch(node.childs, version);
        if (childs !== node.childs) next = { ...next, childs };
      }
      return next;
    });
  let next = patch(tree, 1);
  next = patch(next, 2);
  return next;
}

// #endregion apv2TiTrong
